import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { format } from "date-fns";
import { 
  Image,
  Plus,
  Copy,
  Download,
  Trash2,
  X,
  Loader2,
  Camera,
  Heart,
  Mic,
  MicOff,
  AudioLines,
  Share2,
  Sparkles,
  Zap,
  Check,
  Upload,
  CalendarDays,
  RefreshCw,
  Film
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Badge } from "./components/ui/badge";
import { Dialog, DialogContent } from "./components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "./components/ui/popover";
import { Calendar } from "./components/ui/calendar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to format image source
const getImageSrc = (base64, mimeType = 'image/jpeg') => {
  if (!base64) return '';
  if (base64.startsWith('data:')) return base64;
  return `data:${mimeType};base64,${base64}`;
};

// Robust clipboard copy with fallback for mobile browsers
const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // Clipboard API failed, try fallback
  }
  // Fallback: textarea + execCommand
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.style.position = 'fixed';
  textarea.style.left = '-9999px';
  textarea.style.top = '-9999px';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.focus();
  textarea.select();
  try {
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return true;
  } catch (e) {
    document.body.removeChild(textarea);
    return false;
  }
};

// ============ BOTTOM NAVIGATION ============
const BottomNav = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  const navItems = [
    { icon: Plus, label: "Create", path: "/", isMain: true },
    { icon: Heart, label: "About", path: "/about" },
  ];

  return (
    <nav className="fixed bottom-12 left-0 right-0 glass border-t border-black/5 z-[99999]" data-testid="bottom-navigation">
      <div className="flex items-center justify-around px-2 py-2 max-w-lg mx-auto">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          
          if (item.isMain) {
            return (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                data-testid="nav-create-button"
                className="flex items-center justify-center w-14 h-14 -mt-6 bg-primary text-primary-foreground rounded-full shadow-soft-lg transition-all duration-300 hover:scale-105 active:scale-95"
              >
                <Icon className="w-6 h-6" strokeWidth={1.5} />
              </button>
            );
          }
          
          return (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              data-testid={`nav-${item.label.toLowerCase()}-button`}
              className={`flex flex-col items-center gap-1 px-4 py-2 rounded-2xl transition-all duration-300 ${
                isActive 
                  ? "text-primary" 
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="text-xs font-medium">{item.label}</span>
            </button>
          );
        })}
      </div>
      <div className="h-safe-area-inset-bottom bg-white/70" />
    </nav>
  );
};

// ============ CREATE PAGE ============
// ============ CREATE PAGE ============
const UploadPage = () => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [contextHint, setContextHint] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatedCaption, setGeneratedCaption] = useState(null);
  const [slideshowHooks, setSlideshowHooks] = useState([]);
  const [captionImage, setCaptionImage] = useState(null);
  const [reelVideo, setReelVideo] = useState(null);
  const [reelVideoUrl, setReelVideoUrl] = useState(null);
  const [creatingImage, setCreatingImage] = useState(false);
  const [creatingReel, setCreatingReel] = useState(false);
  const [progressMsg, setProgressMsg] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const navigate = useNavigate();

  const [regenerating, setRegenerating] = useState(false);
  const [keepAudio, setKeepAudio] = useState(false);

  const hasVideo = selectedFiles.some(f => f.isVideo);

  // Poll for background video reel processing
  const pollForReel = async (jobId) => {
    const POLL_INTERVAL = 3000;
    const MAX_POLLS = 60;
    let networkErrors = 0;
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL));
      try {
        const res = await axios.get(`${API}/videos/reel/status/${jobId}`);
        networkErrors = 0; // Reset on success
        if (res.data.status === "complete") {
          return res.data.video_base64;
        }
        if (res.data.status === "failed") {
          throw new Error(res.data.detail || "Video processing failed");
        }
        const elapsed = Math.round((i + 1) * POLL_INTERVAL / 1000);
        setProgressMsg(`Processing your Reel... (${elapsed}s)`);
      } catch (err) {
        if (err.response?.status === 500 && err.response?.data?.detail) {
          throw new Error(err.response.data.detail);
        }
        if (err.response?.status === 404) {
          throw new Error("Processing job expired. Please try again.");
        }
        // Tolerate up to 5 network errors (Cloudflare 520, timeouts, etc.)
        networkErrors++;
        if (networkErrors > 5) throw new Error("Network error during processing. Please try again.");
        const elapsed = Math.round((i + 1) * POLL_INTERVAL / 1000);
        setProgressMsg(`Processing your Reel... (${elapsed}s)`);
      }
    }
    throw new Error("Video processing timed out. Please try again.");
  };

  // Poll for background caption generation
  const pollForCaption = async (jobId) => {
    const POLL_INTERVAL = 2000;
    const MAX_POLLS = 30;
    let networkErrors = 0;
    for (let i = 0; i < MAX_POLLS; i++) {
      await new Promise(r => setTimeout(r, POLL_INTERVAL));
      try {
        const res = await axios.get(`${API}/captions/status/${jobId}`);
        networkErrors = 0;
        if (res.data.status === "complete") {
          return res.data;
        }
        if (res.data.status === "failed") {
          throw new Error(res.data.detail || "Caption generation failed");
        }
        const elapsed = Math.round((i + 1) * POLL_INTERVAL / 1000);
        setProgressMsg(`AI is writing your caption... (${elapsed}s)`);
      } catch (err) {
        if (err.response?.status === 500 && err.response?.data?.detail) {
          throw new Error(err.response.data.detail);
        }
        if (err.response?.status === 404) {
          throw new Error("Job expired. Please try again.");
        }
        networkErrors++;
        if (networkErrors > 5) throw new Error("Network error. Please try again.");
        const elapsed = Math.round((i + 1) * POLL_INTERVAL / 1000);
        setProgressMsg(`AI is writing your caption... (${elapsed}s)`);
      }
    }
    throw new Error("Caption generation timed out. Please try again.");
  };

  // Convert base64 video to Blob URL for reliable mobile playback
  useEffect(() => {
    if (reelVideo) {
      try {
        const byteChars = atob(reelVideo);
        const byteArray = new Uint8Array(byteChars.length);
        for (let i = 0; i < byteChars.length; i++) {
          byteArray[i] = byteChars.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: 'video/mp4' });
        const url = URL.createObjectURL(blob);
        setReelVideoUrl(url);
        return () => URL.revokeObjectURL(url);
      } catch (e) {
        console.error('Video blob creation failed:', e);
      }
    } else {
      setReelVideoUrl(null);
    }
  }, [reelVideo]);

  const isSlideshow = selectedFiles.length > 1;

  const startRecording = () => {
    // Use browser's built-in Speech Recognition (instant, free, works on Safari iOS)
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Speech recognition not supported on this browser.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = 'en-US';

    recognition.onresult = (event) => {
      let text = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        text += event.results[i][0].transcript;
      }
      if (text.trim()) {
        setContextHint(prev => prev ? prev + ' ' + text.trim() : text.trim());
      }
    };

    recognition.onend = () => {
      setIsRecording(false);
    };

    recognition.onerror = (event) => {
      console.error("Speech recognition error:", event.error);
      setIsRecording(false);
      if (event.error === 'not-allowed') {
        toast.error("Microphone access denied. Please allow mic permissions.");
      } else if (event.error === 'no-speech') {
        toast.info("No speech detected. Try again.");
      } else {
        toast.error("Voice capture failed. Try typing instead.");
      }
    };

    setMediaRecorder(recognition);
    recognition.start();
    setIsRecording(true);
    toast.info("Listening... speak now, tap mic to stop");
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      try {
        mediaRecorder.stop();
      } catch (e) {
        // Already stopped
      }
      setIsRecording(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => {
      if (f.type.startsWith('image/')) return true;
      if (f.type.startsWith('video/')) return true;
      return false;
    }).slice(0, 6);
    
    const filePromises = validFiles.map(file => {
      return new Promise((resolve) => {
        if (file.type.startsWith('image/')) {
          const reader = new FileReader();
          reader.onload = (ev) => {
            const dataUrl = ev.target.result;
            const img = new window.Image();
            img.onload = () => {
              const orientation = img.naturalHeight >= img.naturalWidth ? 'portrait' : 'landscape';
              resolve({
                file,
                preview: dataUrl,
                base64: dataUrl.split(',')[1],
                mime_type: file.type,
                isVideo: false,
                orientation,
                width: img.naturalWidth,
                height: img.naturalHeight,
              });
            };
            img.onerror = () => {
              resolve({
                file, preview: dataUrl, base64: dataUrl.split(',')[1],
                mime_type: file.type, isVideo: false, orientation: 'unknown',
              });
            };
            img.src = dataUrl;
          };
          reader.readAsDataURL(file);
        } else {
          // VIDEO: Extract a thumbnail frame using video element + canvas
          const videoUrl = URL.createObjectURL(file);
          const video = document.createElement('video');
          video.preload = 'metadata';
          video.muted = true;
          video.playsInline = true;
          
          let resolved = false;
          const resolveVideo = (thumbDataUrl, w, h) => {
            if (resolved) return;
            resolved = true;
            URL.revokeObjectURL(videoUrl);
            const orientation = (h || 1) >= (w || 1) ? 'portrait' : 'landscape';
            resolve({
              file,
              preview: thumbDataUrl || '',
              base64: thumbDataUrl ? thumbDataUrl.split(',')[1] : '',
              mime_type: 'image/jpeg',
              isVideo: true,
              orientation,
              width: w || 0,
              height: h || 0,
              originalVideoName: file.name,
            });
          };
          
          video.onloadeddata = () => {
            video.currentTime = Math.min(0.5, video.duration / 2);
          };
          
          video.onseeked = () => {
            try {
              const canvas = document.createElement('canvas');
              canvas.width = video.videoWidth || 640;
              canvas.height = video.videoHeight || 480;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
              const thumbDataUrl = canvas.toDataURL('image/jpeg', 0.85);
              resolveVideo(thumbDataUrl, video.videoWidth, video.videoHeight);
            } catch (e) {
              // Canvas draw failed — still add the video
              resolveVideo(null, video.videoWidth, video.videoHeight);
            }
          };
          
          video.onerror = () => {
            // Can't preview the video, but still allow it to be uploaded
            resolveVideo(null, 0, 0);
          };
          
          // Timeout: if nothing fires in 5s, still add the video
          setTimeout(() => resolveVideo(null, 0, 0), 5000);
          
          video.src = videoUrl;
          video.load();
        }
      });
    });

    Promise.all(filePromises).then(results => {
      const valid = results.filter(Boolean);
      setSelectedFiles(prev => [...prev, ...valid].slice(0, 6));
    });
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadAndGenerate = async () => {
    if (selectedFiles.length === 0) return;
    
    setUploading(true);
    try {
      setProgressMsg("Uploading...");

      // Separate videos from images
      const videos = selectedFiles.filter(f => f.isVideo);
      const images = selectedFiles.filter(f => !f.isVideo);

      let allUploaded = [];

      // Upload images via existing bulk endpoint (base64)
      if (images.length > 0) {
        const photos = images.map(f => ({
          filename: f.file.name,
          image_base64: f.base64,
          mime_type: f.mime_type
        }));
        const uploadRes = await axios.post(`${API}/photos/bulk`, photos);
        allUploaded.push(...uploadRes.data);
      }

      // Upload videos via FormData (actual file, not thumbnail)
      for (const vid of videos) {
        setProgressMsg(`Uploading video: ${vid.file.name}...`);
        const formData = new FormData();
        formData.append('file', vid.file);
        const res = await axios.post(`${API}/upload/video`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          timeout: 120000
        });
        allUploaded.push(res.data);
      }

      setUploadedPhotos(allUploaded);
      
      if (selectedFiles.length === 1) {
        // SINGLE PHOTO/VIDEO FLOW
        setProgressMsg("AI is writing your caption...");
        setGenerating(true);
        const captionReq = await axios.post(`${API}/captions/generate`, {
          photo_id: allUploaded[0].id,
          theme: "parenthood",
          tone: "uplifting",
          context: contextHint || null
        });

        let captionData;
        if (captionReq.data.job_id) {
          captionData = await pollForCaption(captionReq.data.job_id);
        } else {
          captionData = captionReq.data;
        }
        setGeneratedCaption(captionData);
        
        setProgressMsg(videos.length > 0 ? "Creating your Video Reel..." : "Creating your Reel...");
        setCreatingReel(true);
        const reelRes = await axios.post(`${API}/videos/reel`, {
          photo_id: allUploaded[0].id,
          hook: captionData.hook,
          description: captionData.caption_text || "",
          mood: captionData.mood || "emotional",
          style: "minimal",
          keep_audio: keepAudio
        }, { timeout: 180000 });

        if (reelRes.data.job_id) {
          setProgressMsg("Processing your Reel...");
          const videoBase64 = await pollForReel(reelRes.data.job_id);
          setReelVideo(videoBase64);
        } else {
          setReelVideo(reelRes.data.video_base64);
        }
        toast.success("Reel ready!");
      } else {
        // MULTI-PHOTO SLIDESHOW FLOW
        setProgressMsg(`AI is creating hooks for ${allUploaded.length} photos...`);
        setGenerating(true);
        const multiRes = await axios.post(`${API}/captions/generate-multi`, {
          photo_ids: allUploaded.map(p => p.id),
          theme: "parenthood",
          tone: "uplifting",
          context: contextHint || null
        });
        
        setSlideshowHooks(multiRes.data.hooks);
        setGeneratedCaption({
          hook: multiRes.data.hooks[0],
          caption_text: multiRes.data.description,
          hashtags: multiRes.data.hashtags || [],
          mood: multiRes.data.mood
        });

        setProgressMsg("Creating slideshow Reel...");
        setCreatingReel(true);
        const segments = allUploaded.map((p, i) => ({
          photo_id: p.id,
          hook: multiRes.data.hooks[i] || `Chapter ${i + 1}`
        }));

        const slideRes = await axios.post(`${API}/videos/slideshow-reel`, {
          segments,
          description: multiRes.data.description,
          mood: multiRes.data.mood || "emotional",
          context: contextHint || ""
        }, { timeout: 180000 });

        setReelVideo(slideRes.data.video_base64);
        toast.success(`Slideshow Reel (${slideRes.data.segment_count} photos) ready!`);
      }
    } catch (e) {
      console.error("Process failed", e);
      if (e.code === 'ECONNABORTED' || e.message?.includes('timeout')) {
        toast.error("Server took too long. Try a smaller photo or try again.");
      } else if (e.response?.status === 520 || e.response?.status === 502 || e.response?.status === 503) {
        toast.error("Server is busy. Please wait 30 seconds and try again.");
      } else if (e.response?.data?.detail) {
        toast.error(e.response.data.detail);
      } else {
        toast.error("Something went wrong. Try again.");
      }
    } finally {
      setUploading(false);
      setGenerating(false);
      setCreatingReel(false);
      setProgressMsg("");
    }
  };

  const postNow = async () => {
    if (!reelVideo || !generatedCaption) return;
    
    // Copy full Instagram caption to clipboard
    const copied = await copyToClipboard(generatedCaption.caption_text);
    
    // Download video using blob URL
    const link = document.createElement('a');
    link.href = reelVideoUrl;
    link.download = `sabhya_saachi_reel_${Date.now()}.mp4`;
    link.click();
    
    copied ? toast.success("Reel downloaded & caption copied!") : toast.success("Reel downloaded!");
  };

  const shareReel = async () => {
    if (!reelVideo) return;
    try {
      // Convert base64 to blob for sharing
      const byteChars = atob(reelVideo);
      const byteNumbers = new Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) {
        byteNumbers[i] = byteChars.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'video/mp4' });
      const file = new File([blob], `sabhya_saachi_reel_${Date.now()}.mp4`, { type: 'video/mp4' });
      
      if (navigator.share && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: 'Sabhya & Saachi Reel',
          text: generatedCaption.caption_text
        });
      } else {
        // Fallback: just download
        postNow();
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error("Share failed:", err);
        postNow(); // Fallback to download
      }
    }
  };

  const resetAll = () => {
    setSelectedFiles([]);
    setUploadedPhotos([]);
    setGeneratedCaption(null);
    setSlideshowHooks([]);
    setCaptionImage(null);
    setReelVideo(null);
    setContextHint("");
    setProgressMsg("");
    setIsRecording(false);
    setRegenerating(false);
    setKeepAudio(false);
  };

  const regenerateCaption = async () => {
    if (!uploadedPhotos.length) return;
    setRegenerating(true);
    setProgressMsg("Regenerating with AI...");
    try {
      if (uploadedPhotos.length === 1) {
        const captionReq = await axios.post(`${API}/captions/generate`, {
          photo_id: uploadedPhotos[0].id,
          theme: "parenthood",
          tone: "uplifting",
          context: contextHint || null
        });

        let captionData;
        if (captionReq.data.job_id) {
          captionData = await pollForCaption(captionReq.data.job_id);
        } else {
          captionData = captionReq.data;
        }
        setGeneratedCaption(captionData);
        
        setProgressMsg("Rebuilding Reel...");
        const reelRes = await axios.post(`${API}/videos/reel`, {
          photo_id: uploadedPhotos[0].id,
          hook: captionData.hook,
          description: captionData.caption_text || "",
          mood: captionData.mood || "emotional",
          style: "minimal",
          keep_audio: keepAudio
        }, { timeout: 180000 });

        if (reelRes.data.job_id) {
          setProgressMsg("Processing your Reel...");
          const videoBase64 = await pollForReel(reelRes.data.job_id);
          setReelVideo(videoBase64);
        } else {
          setReelVideo(reelRes.data.video_base64);
        }
      } else {
        const multiRes = await axios.post(`${API}/captions/generate-multi`, {
          photo_ids: uploadedPhotos.map(p => p.id),
          theme: "parenthood",
          tone: "uplifting",
          context: contextHint || null
        });
        setSlideshowHooks(multiRes.data.hooks);
        setGeneratedCaption({
          hook: multiRes.data.hooks[0],
          caption_text: multiRes.data.description,
          hashtags: multiRes.data.hashtags || [],
          mood: multiRes.data.mood
        });

        setProgressMsg("Rebuilding slideshow...");
        const segments = uploadedPhotos.map((p, i) => ({
          photo_id: p.id,
          hook: multiRes.data.hooks[i] || `Chapter ${i + 1}`
        }));
        const slideRes = await axios.post(`${API}/videos/slideshow-reel`, {
          segments,
          description: multiRes.data.description,
          mood: multiRes.data.mood || "emotional",
          context: contextHint || ""
        }, { timeout: 180000 });
        setReelVideo(slideRes.data.video_base64);
      }
      toast.success("Fresh content generated!");
    } catch (err) {
      console.error("Regenerate failed:", err);
      toast.error("Regeneration failed. Try again.");
    } finally {
      setRegenerating(false);
      setProgressMsg("");
    }
  };

  return (
    <div className="min-h-screen pb-safe" data-testid="upload-page">
      <header className="sticky top-0 glass border-b border-black/5 z-40 px-4 py-4">
        <div className="max-w-lg mx-auto">
          <h1 className="font-heading text-2xl font-light text-slate-800">Create Post</h1>
          <p className="text-sm text-muted-foreground mt-1">Upload → Generate → Post</p>
        </div>
      </header>

      <main className="px-4 py-6 max-w-lg mx-auto space-y-6">
        
        {/* STEP 1: Select Photos (if no reel yet) */}
        {!reelVideo && (
          <>
            <Card className="bg-white shadow-soft border border-black/5 rounded-3xl overflow-hidden">
              <CardContent className="p-6">
                <label 
                  htmlFor="photo-upload"
                  className="block w-full border-2 border-dashed border-primary/30 rounded-2xl p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
                  data-testid="photo-upload-area"
                >
                  <div className="w-14 h-14 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center">
                    <Camera className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <p className="text-slate-700 font-medium mb-1">
                    {selectedFiles.length > 0 
                      ? `${selectedFiles.length} file${selectedFiles.length > 1 ? 's' : ''} selected` 
                      : "Tap to select photos or videos"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {selectedFiles.length > 1 
                      ? `Slideshow Reel (up to 20 sec)` 
                      : selectedFiles.length === 1
                        ? "Single Reel (12 sec)"
                        : "1 photo = Reel | 3-5 photos = Slideshow | Portrait photos work best"}
                  </p>
                  <input
                    id="photo-upload"
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="photo-upload-input"
                  />
                </label>
              </CardContent>
            </Card>

            {/* Selected Files Preview */}
            {selectedFiles.length > 0 && (
              <div className={`flex ${selectedFiles.length > 1 ? 'gap-2 overflow-x-auto' : 'justify-center'}`}>
                {selectedFiles.map((f, idx) => (
                  <div key={idx} className="relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden bg-slate-100">
                    {f.preview ? (
                      <img src={f.preview} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-slate-200">
                        <Film className="w-8 h-8 text-slate-400" />
                      </div>
                    )}
                    <button
                      onClick={() => removeFile(idx)}
                      className="absolute top-1 right-1 w-5 h-5 bg-black/60 rounded-full flex items-center justify-center"
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                    {f.isVideo && (
                      <span className="absolute bottom-1 left-1 text-[10px] bg-black/60 text-white px-1 rounded" data-testid={`video-badge-${idx}`}>VID</span>
                    )}
                    {!f.isVideo && f.orientation === 'portrait' && (
                      <span className="absolute bottom-1 left-1 text-[9px] bg-emerald-600/80 text-white px-1.5 py-0.5 rounded font-medium" data-testid={`orientation-badge-${idx}`}>Portrait</span>
                    )}
                    {!f.isVideo && f.orientation === 'landscape' && (
                      <span className="absolute bottom-1 left-1 text-[9px] bg-amber-500/80 text-white px-1.5 py-0.5 rounded font-medium" data-testid={`orientation-badge-${idx}`}>Landscape</span>
                    )}
                  </div>
                ))}
                {selectedFiles.length < 6 && selectedFiles.length > 0 && (
                  <label 
                    htmlFor="photo-upload-more"
                    className="flex-shrink-0 w-24 h-24 rounded-xl border-2 border-dashed border-primary/30 flex items-center justify-center cursor-pointer hover:border-primary/50"
                  >
                    <Plus className="w-6 h-6 text-primary/50" />
                    <input
                      id="photo-upload-more"
                      type="file"
                      accept="image/*,video/*"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            )}

            {/* Slideshow indicator */}
            {selectedFiles.length > 1 && (
              <p className="text-center text-xs text-primary font-medium">
                Slideshow mode: {selectedFiles.length} segments — each gets its own AI hook
              </p>
            )}

            {/* Landscape warning */}
            {selectedFiles.some(f => f.orientation === 'landscape') && (
              <p className="text-center text-xs text-amber-600 font-medium px-4">
                Landscape photos will be auto-cropped to fit the portrait Reel format. Portrait photos work best.
              </p>
            )}

            {/* Audio option — only when video selected */}
            {hasVideo && (
              <Card className="bg-white shadow-soft border border-black/5 rounded-3xl">
                <CardContent className="p-4">
                  <button
                    onClick={() => setKeepAudio(!keepAudio)}
                    className="w-full flex items-center justify-between"
                    data-testid="keep-audio-toggle"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                        keepAudio ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"
                      }`}>
                        <AudioLines className="w-5 h-5" strokeWidth={1.5} />
                      </div>
                      <div className="text-left">
                        <p className="text-sm font-semibold text-foreground">Keep original audio</p>
                        <p className="text-xs text-muted-foreground">
                          {keepAudio ? "Video's sound will be in the Reel" : "Silent — add trending music on Instagram"}
                        </p>
                      </div>
                    </div>
                    <div className={`w-11 h-6 rounded-full transition-colors relative ${
                      keepAudio ? "bg-primary" : "bg-black/10"
                    }`}>
                      <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                        keepAudio ? "translate-x-5" : "translate-x-0.5"
                      }`} />
                    </div>
                  </button>
                </CardContent>
              </Card>
            )}

            {/* Story Prompt — MANDATORY */}
            {selectedFiles.length > 0 && (
              <Card className="bg-white shadow-soft border border-black/5 rounded-3xl">
                <CardContent className="p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-foreground mb-1">Tell the story behind this moment</p>
                    <p className="text-xs text-muted-foreground leading-relaxed mb-3">
                      Speak or type what's happening — who's in the photo, what they're doing, what makes this moment special. The more you share, the better the AI captures the emotion.
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="e.g., the nanny giving her first bath, both grandmothers oiling the baby..."
                      value={contextHint}
                      onChange={(e) => setContextHint(e.target.value)}
                      className="rounded-xl border-black/10 flex-1"
                      data-testid="context-input"
                      disabled={isRecording}
                    />
                    <button
                      onClick={isRecording ? stopRecording : startRecording}
                      data-testid="voice-prompt-button"
                      className={`flex-shrink-0 w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 ${
                        isRecording 
                          ? "bg-red-500 text-white animate-pulse shadow-lg shadow-red-200" 
                          : "bg-primary/10 text-primary hover:bg-primary/20"
                      }`}
                    >
                      {isRecording ? (
                        <MicOff className="w-5 h-5" strokeWidth={1.5} />
                      ) : (
                        <Mic className="w-5 h-5" strokeWidth={1.5} />
                      )}
                    </button>
                  </div>
                  {isRecording && (
                    <div className="flex items-center gap-2 text-red-500 text-xs font-medium">
                      <AudioLines className="w-4 h-4 animate-pulse" />
                      Listening... speak now, tap mic to stop
                    </div>
                  )}
                  {contextHint && !isRecording && (
                    <p className="text-xs text-emerald-600 font-medium">Your story: {contextHint}</p>
                  )}
                  {!contextHint && !isRecording && (
                    <p className="text-xs text-amber-500 font-medium">Required — tell the story to generate your reel</p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Generate Button — disabled without context */}
            {selectedFiles.length > 0 && (
              <Button 
                onClick={uploadAndGenerate}
                disabled={uploading || generating || creatingReel || !contextHint.trim()}
                className="w-full rounded-full bg-primary hover:bg-primary/90 h-14 text-base disabled:opacity-40"
                data-testid="generate-button"
              >
                {uploading || generating || creatingReel ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    {progressMsg || "Processing..."}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" strokeWidth={1.5} />
                    {selectedFiles.length > 1 
                      ? `Create Slideshow Reel (${selectedFiles.length} photos)` 
                      : "Create Reel"}
                  </>
                )}
              </Button>
            )}
          </>
        )}

        {/* STEP 2: Preview + Download */}
        {reelVideo && generatedCaption && (
          <div className="space-y-5 animate-fade-in">
            {/* Preview Header */}
            <div className="text-center">
              <h2 className="font-heading text-xl text-slate-800 font-medium">Your Reel is Ready</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Download and post on Instagram
              </p>
            </div>

            {/* Video Preview */}
            <div className="rounded-2xl overflow-hidden shadow-soft border border-black/5">
              <video 
                src={reelVideoUrl}
                controls
                autoPlay
                loop
                muted={!keepAudio}
                playsInline
                className="w-full aspect-[9/16] object-cover"
                data-testid="reel-preview-video"
              />
            </div>

            {/* Regenerate — right under the video so user sees it immediately */}
            <Button 
              onClick={regenerateCaption}
              disabled={regenerating}
              variant="outline"
              className="w-full rounded-2xl h-11 border-primary/30 text-primary font-medium"
              data-testid="regenerate-button"
            >
              {regenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {progressMsg || "Regenerating..."}
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Not happy? Regenerate
                </>
              )}
            </Button>

            {/* Share + Download Buttons */}
            <div className="space-y-3">
              <Button 
                onClick={shareReel}
                className="w-full rounded-2xl bg-green-600 hover:bg-green-700 text-white h-16 text-lg font-semibold shadow-lg"
                data-testid="share-reel-button"
              >
                <Share2 className="w-6 h-6 mr-3" strokeWidth={2} />
                Save to Photos / Share
              </Button>
              <p className="text-center text-xs text-muted-foreground -mt-1">
                Save to Camera Roll or share directly to Instagram
              </p>
              <Button 
                onClick={postNow}
                variant="outline"
                className="w-full rounded-2xl h-12 border-black/10 text-sm"
                data-testid="download-reel-button"
              >
                <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Download to Files
              </Button>
            </div>

            {/* Editable Hashtags */}
            <Card className="bg-white shadow-soft border border-black/5 rounded-2xl">
              <CardContent className="p-4 space-y-3">
                {slideshowHooks.length > 1 ? (
                  <div>
                    <p className="text-xs text-primary font-medium mb-2">HOOKS</p>
                    <div className="space-y-1.5">
                      {slideshowHooks.map((h, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="text-xs text-muted-foreground font-mono mt-0.5">{i+1}.</span>
                          <Input
                            value={h}
                            onChange={(e) => {
                              const updated = [...slideshowHooks];
                              updated[i] = e.target.value;
                              setSlideshowHooks(updated);
                            }}
                            className="text-sm font-semibold border-0 bg-transparent p-0 h-7 focus-visible:ring-0"
                            data-testid={`hook-edit-${i}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div>
                    <p className="text-xs text-primary font-medium mb-1">VIDEO TEXT</p>
                    <Textarea 
                      value={generatedCaption.hook}
                      onChange={(e) => setGeneratedCaption({...generatedCaption, hook: e.target.value})}
                      className="text-base font-semibold border-0 bg-transparent p-0 focus-visible:ring-0 min-h-[60px] resize-none leading-relaxed"
                      data-testid="hook-edit-input"
                    />
                  </div>
                )}
                <div className="border-t border-black/5 pt-3">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-muted-foreground font-medium">CAPTION FOR INSTAGRAM</p>
                    <button
                      onClick={async () => {
                        const ok = await copyToClipboard(generatedCaption.caption_text);
                        ok ? toast.success("Caption copied! Paste it on Instagram.") : toast.error("Copy failed — try long-pressing to copy");
                      }}
                      className="text-xs text-primary flex items-center gap-1 font-medium"
                      data-testid="copy-full-caption-button"
                    >
                      <Copy className="w-3 h-3" /> Copy
                    </button>
                  </div>
                  <div className="bg-stone-50/80 rounded-xl p-3 border border-stone-100">
                    <p className="text-sm text-foreground/80 leading-relaxed whitespace-pre-line">{generatedCaption.caption_text}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Start Over */}
            <Button 
              onClick={resetAll}
              variant="ghost"
              className="w-full text-muted-foreground"
              data-testid="reset-button"
            >
              Create Another Reel
            </Button>
          </div>
        )}
      </main>
    </div>
  );
};

// ============ STUDIO PAGE ============
const StudioPage = () => {
  const [photos, setPhotos] = useState([]);
  const [selectedPhotos, setSelectedPhotos] = useState([]);
  const [generatedCaption, setGeneratedCaption] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [scheduledDate, setScheduledDate] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    try {
      const res = await axios.get(`${API}/photos?limit=50`);
      setPhotos(res.data);
    } catch (e) {
      console.error("Failed to fetch photos", e);
    }
  };

  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => 
      prev.includes(photoId) 
        ? prev.filter(id => id !== photoId)
        : [...prev, photoId]
    );
  };

  const deletePhoto = async (photoId, e) => {
    e.stopPropagation(); // Prevent selecting photo when clicking delete
    try {
      await axios.delete(`${API}/photos/${photoId}`);
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setSelectedPhotos(prev => prev.filter(id => id !== photoId));
      toast.success("Photo deleted");
    } catch (err) {
      console.error("Failed to delete photo", err);
      toast.error("Failed to delete photo");
    }
  };

  const clearAllPhotos = async () => {
    if (!window.confirm("Clear entire library? This will delete all photos and posts.")) return;
    try {
      const res = await axios.delete(`${API}/photos/clear-all`);
      setPhotos([]);
      setSelectedPhotos([]);
      setGeneratedCaption(null);
      toast.success(`Library cleared: ${res.data.photos_deleted} photos removed`);
    } catch (err) {
      console.error("Failed to clear library", err);
      toast.error("Failed to clear library");
    }
  };

  const [contextHint, setContextHint] = useState("");

  const generateCaption = async () => {
    if (selectedPhotos.length === 0) {
      toast.error("Please select at least one photo");
      return;
    }

    setGenerating(true);
    setCaptionImage(null);
    try {
      const res = await axios.post(`${API}/captions/generate`, {
        photo_id: selectedPhotos[0],
        theme: "parenthood",
        tone: "uplifting",
        context: contextHint || null
      });
      if (res.data.job_id) {
        // Background processing — poll
        const POLL_INTERVAL = 2000;
        for (let i = 0; i < 30; i++) {
          await new Promise(r => setTimeout(r, POLL_INTERVAL));
          const statusRes = await axios.get(`${API}/captions/status/${res.data.job_id}`);
          if (statusRes.data.status === "complete") {
            setGeneratedCaption(statusRes.data);
            toast.success("Caption generated!");
            return;
          }
          if (statusRes.data.status === "failed") {
            throw new Error(statusRes.data.detail || "Caption generation failed");
          }
        }
        throw new Error("Caption generation timed out");
      } else {
        setGeneratedCaption(res.data);
        toast.success("Caption generated!");
      }
    } catch (e) {
      console.error("Failed to generate caption", e);
      toast.error("Failed to generate caption");
    } finally {
      setGenerating(false);
    }
  };

  const copyCaption = async () => {
    if (!generatedCaption) return;
    const ok = await copyToClipboard(generatedCaption.caption_text);
    ok ? toast.success("Caption copied to clipboard!") : toast.error("Copy failed — try long-pressing to copy");
  };

  const [creatingImage, setCreatingImage] = useState(false);
  const [captionImage, setCaptionImage] = useState(null);

  // Share to Instagram (opens Instagram with image)
  const shareToInstagram = async () => {
    if (!captionImage) {
      toast.error("Create post image first");
      return;
    }
    
    // Copy caption to clipboard first
    await copyToClipboard(generatedCaption.caption_text);
    
    // Download image
    const link = document.createElement('a');
    link.href = `data:image/jpeg;base64,${captionImage}`;
    link.download = `sabhya_saachi_post_${Date.now()}.jpg`;
    link.click();
    
    toast.success("Image downloaded & caption copied! Open Instagram to post.");
    
    // Try to open Instagram (works on mobile)
    setTimeout(() => {
      window.open('instagram://camera', '_blank');
    }, 500);
  };

  const createImageWithCaption = async () => {
    if (!generatedCaption || selectedPhotos.length === 0) return;

    setCreatingImage(true);
    try {
      const res = await axios.post(`${API}/images/with-caption`, {
        photo_id: selectedPhotos[0],
        caption_text: generatedCaption.caption_text,
        hook: generatedCaption.hook,
        style: "minimal"
      });
      setCaptionImage(res.data.image_base64);
      toast.success("Post image created!");
    } catch (e) {
      console.error("Failed to create image", e);
      toast.error("Failed to create post image");
    } finally {
      setCreatingImage(false);
    }
  };

  const downloadImage = () => {
    if (!captionImage) return;
    const link = document.createElement('a');
    link.href = `data:image/jpeg;base64,${captionImage}`;
    link.download = `sabhya_saachi_post_${Date.now()}.jpg`;
    link.click();
    toast.success("Image downloaded!");
  };

  const saveToQueue = async () => {
    if (!generatedCaption || selectedPhotos.length === 0) return;

    setSaving(true);
    try {
      await axios.post(`${API}/posts`, {
        photo_ids: selectedPhotos,
        hook: generatedCaption.hook,
        caption_text: generatedCaption.caption_text,
        hashtags: generatedCaption.hashtags || [],
        scheduled_date: scheduledDate ? scheduledDate.toISOString() : null,
        post_type: selectedPhotos.length > 1 ? "carousel" : "single"
      });
      toast.success("Post added to queue!");
      setGeneratedCaption(null);
      setSelectedPhotos([]);
      setScheduledDate(null);
      setCaptionImage(null);
      setContextHint("");
    } catch (e) {
      console.error("Failed to save post", e);
      toast.error("Failed to save post");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen pb-safe" data-testid="studio-page">
      <header className="sticky top-0 glass border-b border-black/5 z-40 px-4 py-4">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-light text-slate-800">Story Studio</h1>
            <p className="text-sm text-muted-foreground mt-1">Turn moments into inspiration</p>
          </div>
          {selectedPhotos.length > 0 && (
            <Badge className="rounded-full bg-primary text-white">
              {selectedPhotos.length} selected
            </Badge>
          )}
        </div>
      </header>

      <main className="px-4 py-6 max-w-lg mx-auto space-y-6">
        {/* Photo Grid */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg font-medium text-slate-800">Your Photos</h3>
            <div className="flex items-center gap-2">
              {selectedPhotos.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => setSelectedPhotos([])}
                  className="text-muted-foreground"
                  data-testid="clear-photos-selection"
                >
                  Clear selection
                </Button>
              )}
              {photos.length > 0 && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={clearAllPhotos}
                  className="text-red-400 hover:text-red-600 hover:bg-red-50"
                  data-testid="clear-library-button"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
          </div>
          
          {photos.length === 0 ? (
            <Card className="bg-white shadow-soft border border-black/5 rounded-3xl">
              <CardContent className="p-8 text-center">
                <Image className="w-12 h-12 mx-auto text-muted-foreground mb-4" strokeWidth={1.5} />
                <p className="text-muted-foreground">No photos yet. Upload some to get started!</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-3 gap-3">
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  className="relative group"
                >
                  <button
                    onClick={() => togglePhotoSelection(photo.id)}
                    className={`w-full aspect-square rounded-2xl overflow-hidden transition-all duration-200 ${
                      selectedPhotos.includes(photo.id) 
                        ? "ring-2 ring-primary ring-offset-2" 
                        : "hover:opacity-90"
                    }`}
                    data-testid={`photo-${photo.id}`}
                  >
                    <img 
                      src={getImageSrc(photo.image_base64, photo.mime_type)}
                      alt={photo.filename}
                      className="w-full h-full object-cover"
                    />
                    {selectedPhotos.includes(photo.id) && (
                      <div className="absolute inset-0 bg-primary/20 flex items-center justify-center">
                        <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      </div>
                    )}
                  </button>
                  {/* Delete button - visible on hover */}
                  <button
                    onClick={(e) => deletePhoto(photo.id, e)}
                    className="absolute top-1 right-1 w-6 h-6 bg-black/60 hover:bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                    data-testid={`delete-photo-${photo.id}`}
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Generate Button */}
        {selectedPhotos.length > 0 && !generatedCaption && (
          <div className="space-y-3 animate-fade-in">
            {/* Context Hint Input */}
            <Card className="bg-white shadow-soft border border-black/5 rounded-3xl">
              <CardContent className="p-4">
                <label className="text-sm font-medium text-slate-700 mb-2 block">
                  Quick context (optional)
                </label>
                <Input
                  placeholder="e.g., childbirth, first steps, birthday party..."
                  value={contextHint}
                  onChange={(e) => setContextHint(e.target.value)}
                  className="rounded-xl border-black/10 focus:border-primary"
                  data-testid="context-hint-input"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  Help AI understand the moment better
                </p>
              </CardContent>
            </Card>

            <Button 
              onClick={generateCaption}
              disabled={generating}
              className="w-full rounded-full bg-primary hover:bg-primary/90 h-14 text-base"
              data-testid="generate-caption-button"
            >
              {generating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Analyzing & Creating...
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" strokeWidth={1.5} />
                  Generate Caption
                </>
              )}
          </Button>
          </div>
        )}

        {/* Generated Caption Result */}
        {generatedCaption && (
          <section className="space-y-4 animate-slide-up">
            {/* HOOK - Text ON the image */}
            <Card className="bg-gradient-to-br from-primary/10 to-secondary/10 shadow-soft border border-black/5 rounded-3xl overflow-hidden">
              <CardContent className="p-6 space-y-3">
                <div className="flex items-center gap-2">
                  <Zap className="w-5 h-5 text-primary" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg font-medium text-slate-800">Hook (ON the image)</h3>
                </div>
                <p className="text-xs text-muted-foreground">Short, punchy text that appears ON your photo</p>
                <Input 
                  value={generatedCaption.hook || ""}
                  onChange={(e) => setGeneratedCaption({...generatedCaption, hook: e.target.value})}
                  className="text-xl font-bold rounded-xl border-black/10 focus:border-primary bg-white"
                  placeholder="e.g., The moment we became three"
                  data-testid="hook-input"
                />
              </CardContent>
            </Card>

            {/* DESCRIPTION - Text below the post */}
            <Card className="bg-white shadow-soft border border-black/5 rounded-3xl overflow-hidden">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-heading text-lg font-medium text-slate-800">Description (below post)</h3>
                  <div className="flex items-center gap-2">
                    <Badge className="rounded-full bg-secondary/10 text-secondary text-xs">
                      {generatedCaption.mood}
                    </Badge>
                    <Badge className="rounded-full bg-primary/10 text-primary text-xs">
                      {generatedCaption.theme}
                    </Badge>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">Full caption optimized for Instagram reach — copy and paste directly</p>
                
                <Textarea 
                  value={generatedCaption.caption_text}
                  onChange={(e) => setGeneratedCaption({...generatedCaption, caption_text: e.target.value})}
                  className="min-h-[140px] rounded-2xl border-black/10 focus:border-primary resize-none text-sm leading-relaxed"
                  data-testid="caption-textarea"
                />

                <div className="flex gap-2">
                  <Button 
                    onClick={copyCaption}
                    variant="outline"
                    className="flex-1 rounded-full h-11 border-black/10"
                    data-testid="copy-caption-button"
                  >
                    <Copy className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    Copy
                  </Button>
                  <Button 
                    onClick={createImageWithCaption}
                    disabled={creatingImage}
                    className="flex-1 rounded-full h-11 bg-primary hover:bg-primary/90"
                    data-testid="create-post-image-button"
                  >
                    {creatingImage ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Image className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    )}
                    Create Post
                  </Button>
                </div>

                {/* Caption on Image Result */}
                {captionImage && (
                  <div className="mt-4 animate-fade-in space-y-4">
                    <button 
                      onClick={downloadImage}
                      className="w-full rounded-2xl overflow-hidden shadow-soft border border-black/5"
                      data-testid="download-post-image"
                    >
                      <img 
                        src={`data:image/jpeg;base64,${captionImage}`}
                        alt="Post with caption"
                        className="w-full aspect-square object-cover"
                      />
                    </button>
                    
                    {/* Post Now & Schedule Buttons */}
                    <div className="flex gap-2">
                      <Button 
                        onClick={shareToInstagram}
                        className="flex-1 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white h-12"
                        data-testid="post-now-button"
                      >
                        <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Post Now
                      </Button>
                      <Button 
                        onClick={() => {
                          saveToQueue();
                        }}
                        variant="outline"
                        className="flex-1 rounded-full h-12 border-black/10"
                        data-testid="schedule-button"
                      >
                        <CalendarDays className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Schedule
                      </Button>
                    </div>
                    <p className="text-xs text-center text-muted-foreground">
                      Post Now = downloads image & copies caption
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Schedule & Save */}
            <Card className="bg-white shadow-soft border border-black/5 rounded-3xl">
              <CardContent className="p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <CalendarDays className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
                  <span className="text-sm text-slate-700">Schedule for</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="rounded-full border-black/10 text-sm h-9"
                        data-testid="schedule-date-button"
                      >
                        {scheduledDate ? format(scheduledDate, "MMM d, yyyy") : "Select date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 rounded-2xl" align="start">
                      <Calendar
                        mode="single"
                        selected={scheduledDate}
                        onSelect={setScheduledDate}
                        disabled={(date) => date < new Date()}
                        data-testid="schedule-calendar"
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                
                <Button 
                  onClick={saveToQueue}
                  disabled={saving}
                  className="w-full rounded-full bg-primary hover:bg-primary/90 h-12"
                  data-testid="save-to-queue-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    "Add to Queue"
                  )}
                </Button>
              </CardContent>
            </Card>
          </section>
        )}
      </main>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-sm rounded-3xl p-0 overflow-hidden">
          <div className="bg-white">
            {/* Instagram-style header */}
            <div className="flex items-center gap-3 p-4 border-b border-black/5">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-secondary" />
              <span className="font-medium text-sm">sabhya.saachi</span>
            </div>
            
            {/* Image */}
            <div className="aspect-square bg-muted">
              {selectedPhotos.length > 0 && photos.length > 0 && (
                <img 
                  src={(() => {
                    const photo = photos.find(p => p.id === selectedPhotos[0]);
                    if (!photo) return '';
                    return getImageSrc(photo.image_base64, photo.mime_type);
                  })()}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
              )}
            </div>
            
            {/* Caption */}
            <div className="p-4">
              <p className="text-sm">
                <span className="font-medium mr-2">sabhya.saachi</span>
                {generatedCaption?.caption_text?.split('\n')[0]}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============ ABOUT PAGE ============
const SettingsPage = () => {
  return (
    <div className="min-h-screen pb-safe" data-testid="about-page">
      <header className="sticky top-0 glass border-b border-black/5 z-40 px-4 py-4">
        <div className="max-w-lg mx-auto">
          <h1 className="font-heading text-2xl font-light text-slate-800">About</h1>
          <p className="text-sm text-muted-foreground mt-1">@sabhya.saachi.series</p>
        </div>
      </header>

      <main className="px-4 py-6 max-w-lg mx-auto space-y-4">
        <Card className="bg-white shadow-soft border border-black/5 rounded-3xl">
          <CardContent className="p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 mx-auto mb-3 flex items-center justify-center">
              <Heart className="w-8 h-8 text-primary" strokeWidth={1.5} />
            </div>
            <h2 className="font-heading text-xl font-medium text-slate-800">Sabhya & Saachi</h2>
            <p className="text-sm text-muted-foreground mt-1">Content Studio</p>
            <p className="text-xs text-muted-foreground mt-4">
              Upload photos, AI creates branded Reels with parenting tips. Download and post to Instagram with your favorite trending music.
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white shadow-soft border border-black/5 rounded-3xl">
          <CardContent className="p-5">
            <h3 className="font-heading text-lg font-medium text-slate-800 mb-3">How It Works</h3>
            <div className="space-y-3 text-sm text-muted-foreground">
              <div className="flex gap-3 items-start">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">1</span>
                <p>Select photos and describe the topic/context</p>
              </div>
              <div className="flex gap-3 items-start">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">2</span>
                <p>AI generates a compelling parenting tip/hook</p>
              </div>
              <div className="flex gap-3 items-start">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">3</span>
                <p>Professional branded Reel is created automatically</p>
              </div>
              <div className="flex gap-3 items-start">
                <span className="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">4</span>
                <p>Download, add trending music on Instagram, and post!</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

// Error Boundary to prevent blank white screens
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, errorInfo) {
    console.error("App crash:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: 40, textAlign: 'center', fontFamily: 'system-ui' }}>
          <h2 style={{ color: '#333', marginBottom: 12 }}>Something went wrong</h2>
          <p style={{ color: '#888', marginBottom: 20 }}>Please refresh the page to try again.</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '10px 24px', background: '#C4956A', color: 'white', border: 'none', borderRadius: 12, cursor: 'pointer', fontSize: 16 }}
          >
            Refresh Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

function App() {
  return (
    <ErrorBoundary>
    <div className="App font-body">
      <Toaster 
        position="top-center" 
        toastOptions={{
          className: 'rounded-2xl',
          style: {
            background: 'white',
            border: '1px solid rgba(0,0,0,0.05)',
            boxShadow: '0 8px 30px rgb(0 0 0 / 0.04)',
          },
        }}
      />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<UploadPage />} />
          <Route path="/upload" element={<UploadPage />} />
          <Route path="/about" element={<SettingsPage />} />
        </Routes>
        <BottomNav />
      </BrowserRouter>
    </div>
    </ErrorBoundary>
  );
}

export default App;
